import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, Star, Trash2, X, Search } from "lucide-react";

interface TierLink {
  id: number;
  tierId: number;
  tierName: string;
  tierStatus: string;
  createdAt: string;
}

export function OfferTiersTab({ offerId }: { offerId: string }) {
  const queryClient = useQueryClient();
  const [showPicker, setShowPicker] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["offers", offerId, "tiers"],
    queryFn: async () => {
      const res = await api.get(`/v1/offers/${offerId}/tiers`);
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const tierLinks: TierLink[] = data?.data ?? [];

  const removeMutation = useMutation({
    mutationFn: async (tierId: number) => {
      const res = await api.delete(`/v1/offers/${offerId}/tiers/${tierId}`);
      if (!res.ok) throw new Error("Failed");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["offers", offerId, "tiers"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-gray-500">
            Tiers assigned to this offer override its default payout for affiliates in those tiers.
          </p>
        </div>
        <button onClick={() => setShowPicker(true)}
          className="inline-flex items-center gap-2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 shrink-0">
          <Plus className="w-4 h-4" /> Assign Tier
        </button>
      </div>

      {isLoading ? (
        <div className="h-32 bg-gray-100 rounded-xl animate-pulse" />
      ) : tierLinks.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
          <Star className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No tiers assigned.</p>
          <p className="text-xs text-gray-400 mt-1">Assign a tier to override the default payout for its members.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Tier</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Assigned</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tierLinks.map((link) => (
                <tr key={link.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <Link to={`/tiers/${link.tierId}` as any} className="text-sm font-medium text-blue-600 hover:underline">
                      {link.tierName}
                    </Link>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={link.tierStatus} /></td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(link.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => { if (window.confirm(`Remove tier "${link.tierName}" from this offer?`)) removeMutation.mutate(link.tierId); }}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showPicker && (
        <TierPickerModal
          offerId={offerId}
          existingTierIds={tierLinks.map((l) => l.tierId)}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

function TierPickerModal({ offerId, existingTierIds, onClose }: { offerId: string; existingTierIds: number[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  const { data } = useQuery({
    queryKey: ["tiers", { search, pageSize: 100 }],
    queryFn: async () => {
      const qp: Record<string, string> = { pageSize: "100" };
      if (search) qp.search = search;
      const res = await api.get("/v1/tiers", qp);
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const tiers: Array<{ id: number; name: string; status: string; affiliateCount: number }> = (data?.data ?? []).filter(
    (t: any) => !existingTierIds.includes(t.id)
  );

  function toggle(id: number) {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  async function handleAssign() {
    if (selectedIds.length === 0) return;
    setLoading(true);
    try {
      const res = await api.post(`/v1/offers/${offerId}/tiers`, { tierIds: selectedIds });
      if (!res.ok) throw new Error("Failed");
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "tiers"] });
      onClose();
    } catch { } finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Assign Tiers to Offer</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search tiers..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900" />
        </div>
        <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
          {tiers.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-400">No tiers available</div>
          ) : tiers.map((t) => (
            <label key={t.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={selectedIds.includes(t.id)} onChange={() => toggle(t.id)}
                className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900" />
              <div className="flex-1">
                <span className="text-sm text-gray-900">{t.name}</span>
                <span className="text-xs text-gray-400 ml-2">{t.affiliateCount} affiliate{t.affiliateCount !== 1 ? "s" : ""}</span>
              </div>
              <StatusBadge status={t.status} />
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleAssign} disabled={loading || selectedIds.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
            {loading ? "Assigning..." : `Assign ${selectedIds.length} Tier${selectedIds.length !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
